import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { PRO_FEATURES, PRO_PRICING } from "@/constants/pro-features";
import { getProFunnelContent } from "@/constants/pro-funnels";
import { billingService } from "@/services/api/billing.service";
import {
  trackBeginCheckout,
  trackProFunnelView,
} from "@/services/firebase/analytics.service";
import { Button } from "@/components/atoms";
import type { ProFeatureId } from "@/types/billing.types";

export type ProUpgradeFunnelVariant = "hero" | "card" | "compact";

export interface ProUpgradeFunnelProps {
  featureId?: ProFeatureId;
  variant?: ProUpgradeFunnelVariant;
  title?: string;
  description?: string;
  className?: string;
  showFeatureGrid?: boolean;
}

function ProCheckoutActions({
  ctaLabel,
  size = "md",
  featureId,
}: {
  ctaLabel: string;
  size?: "md" | "lg";
  featureId?: ProFeatureId | "billing" | "default";
}) {
  const [interval, setInterval] = useState<"month" | "year">("month");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCheckout = async () => {
    setLoading(true);
    setError(null);
    try {
      sessionStorage.setItem("burnchart_checkout_interval", interval);
      void trackBeginCheckout(interval, featureId);
      const session = await billingService.createCheckoutSession(interval);
      window.location.assign(session.url);
    } catch (checkoutError) {
      console.error(checkoutError);
      setError("Could not start checkout. Try again or open Billing in Settings.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-[14px] bg-surface/30 p-1 ring-1 ring-white/10">
          <button
            type="button"
            onClick={() => setInterval("month")}
            className={`rounded-[10px] px-3 py-1.5 text-sm font-semibold transition-colors ${
              interval === "month"
                ? "bg-brand text-surface"
                : "text-white/70 hover:text-white"
            }`}
          >
            ${PRO_PRICING.monthlyUsd}/mo
          </button>
          <button
            type="button"
            onClick={() => setInterval("year")}
            className={`rounded-[10px] px-3 py-1.5 text-sm font-semibold transition-colors ${
              interval === "year"
                ? "bg-brand text-surface"
                : "text-white/70 hover:text-white"
            }`}
          >
            ${PRO_PRICING.yearlyUsd}/yr
            <span className="ml-1 text-[11px] font-bold opacity-90">
              Save {PRO_PRICING.yearlyDiscountPercent}%
            </span>
          </button>
        </div>

        <Button
          type="button"
          variant="primary"
          size={size}
          onClick={handleCheckout}
          disabled={loading}
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Redirecting…
            </>
          ) : (
            ctaLabel
          )}
        </Button>

        <Link
          to="/settings/billing"
          className="text-sm font-semibold text-white/55 underline-offset-2 hover:text-white hover:underline"
        >
          Billing details
        </Link>
      </div>

      <p className="text-xs text-white/45">
        Secure checkout via Stripe · Cancel anytime from billing settings
      </p>

      {error ? <p className="text-sm text-danger">{error}</p> : null}
    </div>
  );
}

export function ProUpgradeFunnel({
  featureId,
  variant = "card",
  title,
  description,
  className = "",
  showFeatureGrid,
}: ProUpgradeFunnelProps) {
  const funnel = getProFunnelContent(featureId);
  const Icon = funnel.icon;
  const headline = title ?? funnel.headline;
  const subheadline = description ?? funnel.subheadline;
  const showGrid = showFeatureGrid ?? variant !== "compact";

  useEffect(() => {
    void trackProFunnelView(featureId ?? "default", variant);
  }, [featureId, variant]);

  const resolvedFeatureId = featureId ?? "default";

  if (variant === "hero") {
    return (
      <section
        className={`relative isolate overflow-hidden rounded-[24px] border border-brand/25 bg-gradient-to-br from-brand/10 via-surface-elevated to-focus/5 ring-1 ring-brand/20 ${className}`}
      >
        <div className="pointer-events-none absolute inset-0 opacity-80" aria-hidden>
          <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-brand/20 blur-[80px]" />
          <div className="absolute -bottom-12 left-8 h-40 w-40 rounded-full bg-focus/15 blur-[70px]" />
        </div>

        <div className="relative p-6 sm:p-8 lg:p-10">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0 flex-1 space-y-5">
              <div className="inline-flex items-center gap-2 rounded-full bg-brand/15 px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-brand ring-1 ring-brand/25">
                <Sparkles className="h-3.5 w-3.5" />
                {funnel.kicker}
              </div>

              <div className="flex items-start gap-4">
                <div className="hidden rounded-2xl bg-brand/15 p-3 text-brand sm:block">
                  <Icon className="h-7 w-7" />
                </div>
                <div>
                  <h2 className="text-2xl font-black tracking-tight text-white sm:text-3xl">
                    {headline}
                  </h2>
                  <p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/65 sm:text-base">
                    {subheadline}
                  </p>
                </div>
              </div>

              <ul className="grid gap-2.5 sm:grid-cols-2">
                {funnel.bullets.map((bullet) => (
                  <li
                    key={bullet}
                    className="flex items-start gap-2.5 rounded-[14px] bg-surface/25 px-3 py-2.5 text-sm text-white/80"
                  >
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>

              {showGrid ? (
                <div className="rounded-[16px] border border-white/10 bg-surface/20 p-4">
                  <p className="text-[11px] font-black uppercase tracking-wide text-white/45">
                    Everything in Pro
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {PRO_FEATURES.map((feature) => (
                      <span
                        key={feature.id}
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${
                          feature.id === featureId
                            ? "bg-brand/20 text-brand ring-1 ring-brand/30"
                            : "bg-white/5 text-white/60"
                        }`}
                      >
                        {feature.title}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="w-full shrink-0 rounded-[20px] border border-white/10 bg-surface/30 p-5 ring-1 ring-white/[0.06] lg:max-w-sm">
              <p className="text-sm font-bold text-white">Start Pro today</p>
              <p className="mt-1 text-xs text-white/50">
                From ${PRO_PRICING.monthlyUsd}/month · save{" "}
                {PRO_PRICING.yearlyDiscountPercent}% yearly
              </p>
              <div className="mt-4">
                <ProCheckoutActions
                  ctaLabel={funnel.cta}
                  size="lg"
                  featureId={resolvedFeatureId}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (variant === "compact") {
    return (
      <div
        className={`rounded-[20px] border border-brand/25 bg-gradient-to-br from-brand/10 via-surface-elevated to-focus/5 p-5 ring-1 ring-brand/20 ${className}`}
      >
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-brand/15 p-2.5 text-brand">
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-black uppercase tracking-wide text-brand">
              {funnel.kicker}
            </p>
            <h3 className="mt-1 text-lg font-bold text-white">{headline}</h3>
            <p className="mt-1 text-sm text-white/65">{subheadline}</p>
          </div>
        </div>
        <div className="mt-4">
          <ProCheckoutActions ctaLabel={funnel.cta} featureId={resolvedFeatureId} />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`rounded-[20px] border border-brand/25 bg-gradient-to-br from-brand/10 via-surface-elevated to-focus/5 p-6 ring-1 ring-brand/20 ${className}`}
    >
      <div className="flex items-start gap-3">
        <div className="rounded-xl bg-brand/15 p-2.5 text-brand">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-black uppercase tracking-wide text-brand">
            {funnel.kicker}
          </p>
          <h3 className="mt-1 text-xl font-bold text-white">{headline}</h3>
          <p className="mt-2 text-sm text-white/65">{subheadline}</p>
        </div>
      </div>

      <ul className="mt-5 space-y-2">
        {funnel.bullets.map((bullet) => (
          <li key={bullet} className="flex items-start gap-2 text-sm text-white/75">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
            <span>{bullet}</span>
          </li>
        ))}
      </ul>

      {showGrid ? (
        <ul className="mt-5 grid gap-2 sm:grid-cols-2">
          {PRO_FEATURES.map((feature) => (
            <li
              key={feature.id}
              className={`rounded-[14px] px-3 py-2 text-sm ${
                feature.id === featureId
                  ? "bg-brand/15 text-white ring-1 ring-brand/30"
                  : "bg-surface/20 text-white/75"
              }`}
            >
              <span className="font-semibold">{feature.title}</span>
              <span className="mt-0.5 block text-xs text-white/55">
                {feature.description}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="mt-5">
        <ProCheckoutActions ctaLabel={funnel.cta} featureId={resolvedFeatureId} />
      </div>
    </div>
  );
}
